import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
	AuthPageLayout,
	LoginForm,
	MfaChallengeForm,
	useAuth,
} from '@ekhodealer/ekho-common/auth-client';

import type { AdminGetMeResponse } from '@ekhodealer/ekho-common';

import { getPostLoginRedirect } from '@common/lib/authRouting';
import { paths } from '@config/paths';
import { apiClient } from '@config/server/apiClient';
import { queryClient } from '@config/server/queryClient';
import { queries } from '@config/server/queryKeys';

const RECENT_SERVICES_PROVIDER_KEY = 'recentServicesProviderId';

/**
 * Returns a safe redirect path that avoids the `/` -> `/login` infinite loop.
 * When an authenticated user with completed onboarding lands on `/login`, they
 * need to be redirected somewhere. If a cached provider exists in localStorage,
 * `/` is safe (the root loader will fast-path to orders). If not, we redirect
 * to `/select-provider` so DefaultRedirect can fetch and cache a provider.
 */
function getSafeHomeRedirect(): string {
	const recentProvider = localStorage.getItem(RECENT_SERVICES_PROVIDER_KEY);
	if (recentProvider) {
		return paths.home.getHref();
	}
	return '/select-provider';
}

/**
 * Helper function to fetch /me data (admin portal endpoint returns AdminGetMeResponse)
 */
const getMe = async (): Promise<AdminGetMeResponse> => {
	const response = await apiClient.get<AdminGetMeResponse>('/admin_portal/auth/me');
	return response.data;
};

/**
 * LoginPage
 *
 * Handles the complete login flow including MFA challenge as a login state.
 * When MFA is required after primary auth, the page renders MfaChallengeForm
 * instead of navigating to a separate route.
 */
export default function LoginPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const returnUrl = searchParams.get('returnUrl');

	const { mfaPending, mfaResolver, isAuthenticated, isLoading, me, isSigningIn } = useAuth();
	const [isCheckingRedirect, setIsCheckingRedirect] = useState(false);

	// If MFA challenge is pending (user completed password auth but needs MFA),
	// render the MFA challenge form on the same /login route
	const showMfaChallenge = mfaPending && mfaResolver;

	// Handle successful login navigation
	const handleLoginSuccess = useCallback(async () => {
		setIsCheckingRedirect(true);
		try {
			// If we already have /me data in state, use it; otherwise fetch and cache.
			// Admin portal always uses admin endpoint, so me is AdminGetMeResponse.
			const meData: AdminGetMeResponse = me
				? (me as AdminGetMeResponse)
				: await queryClient.fetchQuery({
						queryKey: queries.auth.me.queryKey,
						queryFn: getMe,
					});

			// If returnUrl is provided (e.g., from re-auth redirect), use it directly
			if (returnUrl) {
				navigate(returnUrl, { replace: true });
				return;
			}

			const rawRedirectPath = getPostLoginRedirect(meData);
			// If redirectPath is home (/), use getSafeHomeRedirect to avoid
			// the / -> /login infinite loop when no provider is cached
			const redirectPath =
				rawRedirectPath === paths.home.getHref() ? getSafeHomeRedirect() : rawRedirectPath;

			// Check if there was a "from" location they were trying to access
			const from = (location.state as { from?: { pathname: string } })?.from?.pathname;

			// If they have portal access and were trying to go somewhere specific, honor that
			if (from && meData.canAccessAdminPortal && redirectPath === getSafeHomeRedirect()) {
				navigate(from, { replace: true });
			} else {
				navigate(redirectPath, { replace: true });
			}
		} catch {
			// Navigate to select-provider (not home) to avoid redirect loop
			navigate(getSafeHomeRedirect(), { replace: true });
		}
	}, [me, returnUrl, location.state, navigate]);

	// Handle MFA challenge success
	const handleMfaChallengeSuccess = useCallback(() => {
		navigate(getSafeHomeRedirect(), { replace: true });
	}, [navigate]);

	// Handle MFA cancel
	const handleMfaChallengeCancel = useCallback(() => {
		navigate(paths.login.getHref(), { replace: true });
	}, [navigate]);

	// Handle forgot password
	const handleForgotPassword = useCallback(() => {
		navigate(paths.forgotPassword.getHref());
	}, [navigate]);

	// Compute initial auth loading (excludes active sign-in attempts)
	const isInitialAuthLoading = isLoading && !isSigningIn;

	// On mount, if user is already authenticated, redirect
	useEffect(() => {
		const checkAuthenticatedRedirect = async () => {
			if (!isAuthenticated || isInitialAuthLoading) return;

			setIsCheckingRedirect(true);

			try {
				await handleLoginSuccess();
			} catch {
				// Navigate to select-provider (not home) to avoid redirect loop
				navigate(getSafeHomeRedirect(), { replace: true });
			}
		};

		checkAuthenticatedRedirect();
	}, [isAuthenticated, isInitialAuthLoading, me, navigate, handleLoginSuccess]);

	// While checking initial auth or redirect, show nothing
	if (isInitialAuthLoading || isCheckingRedirect) {
		return null;
	}

	// If already authenticated, useEffect will handle redirect - show nothing to avoid flash
	if (isAuthenticated && !isSigningIn) {
		return null;
	}

	return (
		<AuthPageLayout
			showPoweredBy={false}
			bannerHeadline='AI-powered vehicle commerce platform.'
			bannerDescription='Ekho combines AI sales assistants, inventory tools, and commerce infrastructure so your team can sell more vehicles with less effort.'>
			{showMfaChallenge ? (
				<MfaChallengeForm
					onSuccess={handleMfaChallengeSuccess}
					onCancel={handleMfaChallengeCancel}
				/>
			) : (
				<LoginForm
					details='Your AI-powered command center for inventory, leads, conversations, and sales.'
					onLoginSuccess={handleLoginSuccess}
					onForgotPassword={handleForgotPassword}
				/>
			)}
		</AuthPageLayout>
	);
}
